import { useCallback, useEffect, useMemo, useState } from 'react'
import { createContainer } from 'unstated-next'

import { useHeap } from '../../../../../../app/_components/chrome/scripts/HeapScript'
import { FitModel, ProductAsset } from '../../../../../../types/graphql'
import PinchZoom from '../../ImageZoom/PinchZoom'

// TODO: This needs to be fixed... it is not a safe use of `as`
const sourceToProductAsset = (source: string): ProductAsset => {
  return { source, type: 'ProductImage' } as ProductAsset
}

type PinchZoomOffset = {
  x: number
  y: number
}

// PinchZoom exposes only two properties even though in reality there are more
export interface ExtendedPinchZoom extends PinchZoom {
  addOffset: (offset: PinchZoomOffset) => void
  lastScale: number
  offset: PinchZoomOffset
  zoomFactor: number
  zoomOutAnimation: Function
}

export type GalleryImage = {
  __typename?: 'ProductAsset'
  index: number
  source: string
  kind: string | null
  type: string | null
}

export type GalleryVideo = {
  __typename?: 'ProductAsset'
  kind: string | null
  source: string
  type: string | null
}

export type GalleryAsset = GalleryImage | GalleryVideo

interface IProps {
  activeFitModel: FitModel | null | undefined
  currentAssetIndex: number
  detailImages: Array<string>
  images: Array<GalleryImage>
  isImageZoomShown: boolean
  videos: Array<GalleryVideo>
}

const defaultProps: IProps = {
  activeFitModel: undefined,
  currentAssetIndex: 0,
  detailImages: [],
  images: [],
  isImageZoomShown: false,
  videos: [],
}

// TODO: This should likely leverage a reducer to clean up all the state setting functions.
function useGalleryImageState({
  activeFitModel,
  currentAssetIndex: initialAssetIndex = defaultProps.currentAssetIndex,
  detailImages = defaultProps.detailImages,
  images = defaultProps.images,
  isImageZoomShown: initialIsImageZoomShown = defaultProps.isImageZoomShown,
  videos = defaultProps.videos,
}: Partial<IProps> = defaultProps) {
  const allAssets = useMemo<Array<GalleryAsset>>(() => {
    const fitModelImageAssets = activeFitModel?.images?.map(sourceToProductAsset)
    const detailImageAssets = detailImages.map(sourceToProductAsset)
    return [
      ...(fitModelImageAssets && fitModelImageAssets.length > 0 ? fitModelImageAssets : images),
      ...(detailImageAssets && detailImageAssets.length > 0 ? detailImageAssets : []),
      ...videos,
    ]
  }, [activeFitModel, detailImages, images, videos])

  const defaultPinchZooms = useMemo(() => {
    return Array.from({ length: allAssets.length }).fill(null) as Array<null>
  }, [allAssets.length])

  const { sendHeapEvent } = useHeap()
  const [currentAssetIndex, setCurrentAssetIndex] = useState(() => {
    const initialActiveImageEvent = {
      eventName: 'gallery-image-change-view',
      props: {
        'gallery-image-new-index': String(initialAssetIndex),
        'gallery-image-source': String(allAssets[initialAssetIndex]?.source),
      },
    }
    sendHeapEvent(initialActiveImageEvent)
    return initialAssetIndex
  })
  const [isZoomed, setIsZoomed] = useState(false)
  const [shouldAnimateCarousel, setShouldAnimateCarousel] = useState(true)
  const [isImageZoomShown, setIsImageZoomShown] = useState(initialIsImageZoomShown)
  const [pinchZoomNodes, setPinchZoomNodes] =
    useState<Array<ExtendedPinchZoom | null>>(defaultPinchZooms)

  const setCurrentImageIndex = useCallback(
    (index: number, options?: { preventAnimation?: boolean }) => {
      setCurrentAssetIndex(index)
      const indexChangedEvent = {
        eventName: 'gallery-image-change-view',
        props: {
          'gallery-image-new-index': String(index),
          'gallery-image-source': String(allAssets[index]?.source),
        },
      }
      sendHeapEvent(indexChangedEvent)
      if (options?.preventAnimation !== undefined) {
        setShouldAnimateCarousel(options.preventAnimation)
      }
      const timeout = setTimeout(() => {
        setShouldAnimateCarousel(true)
      }, 50)
      return () => clearTimeout(timeout)
    },
    [sendHeapEvent, allAssets]
  )

  function showImageZoom(index?: number): void {
    setCurrentAssetIndex(index ?? currentAssetIndex)
    setIsImageZoomShown(true)
  }

  const hideImageZoom = useCallback((): void => {
    setIsZoomed(false)
    setIsImageZoomShown(false)
    setPinchZoomNodes(defaultPinchZooms)
  }, [defaultPinchZooms])

  function setPinchZoomNode(index: number, value: ExtendedPinchZoom): void {
    if (isImageZoomShown) {
      pinchZoomNodes[index] = value
      setPinchZoomNodes([...pinchZoomNodes])
    }
  }

  function revealNextSlide(): void {
    if (allAssets.length > 1) {
      const isLastSlide = currentAssetIndex === allAssets.length - 1
      const newSlide = isLastSlide ? 0 : currentAssetIndex + 1

      setCurrentAssetIndex(newSlide)
    }
  }

  function revealPreviousSlide(): void {
    if (allAssets.length > 1) {
      const isFirstSlide = currentAssetIndex === 0
      const newSlide = isFirstSlide ? allAssets.length - 1 : currentAssetIndex - 1
      setCurrentAssetIndex(newSlide)
    }
  }

  useEffect(() => {
    const cleanup = setCurrentImageIndex(initialAssetIndex, { preventAnimation: true })
    return cleanup
  }, [activeFitModel, allAssets.length, initialAssetIndex, setCurrentImageIndex])

  return {
    allAssets,
    currentAssetIndex,
    hideImageZoom,
    isImageZoomShown,
    isZoomed,
    pinchZoomNodes,
    revealNextSlide,
    revealPreviousSlide,
    setCurrentImageIndex,
    setIsZoomed,
    setPinchZoomNode,
    shouldAnimateCarousel,
    showImageZoom,
  }
}

export const GalleryImageStateContainer = createContainer(useGalleryImageState)
